<template>
    <div class="table-container w-full">
        <div class="mt-1 flex justify-between lg:flex-row flex-col">
            <div class="lg:w-1/3 w-full lg:mr-2 mb-2 lg:mb-0">
                <a-input-search
                    data-cy="upload-history-input-search"
                    @search="onSearch"
                    :placeholder="$t('globals.tables.search')"
                />
            </div>
            <div
                class="lg:w-1/2 xl:w-2/5 w-full lg:mr-2 mb-2 lg:mb-0"
                v-if="showFilter"
                data-cy="upload-history-logs-filter"
            >
                <a-select
                    show-search
                    :placeholder="$t('globals.tables.filter')"
                    style="width: 100%"
                    :filter-option="false"
                    :not-found-content="null"
                    @search="handleFilterSearch"
                    @change="handleFilterChange"
                    :allowClear="true"
                >
                    <a-select-opt-group
                        v-if="
                            company_list.some(
                                (value) =>
                                    value.association_code.toLowerCase().includes(filter_value.toLowerCase()) ||
                                    value.company_name.toLowerCase().includes(filter_value.toLowerCase()),
                            )
                        "
                        label="Company"
                    >
                        <template v-for="(value, index) in company_list">
                            <a-select-option
                                v-if="
                                    value.association_code.toLowerCase().includes(filter_value.toLowerCase()) ||
                                    value.company_name.toLowerCase().includes(filter_value.toLowerCase())
                                "
                                :key="`${index}-company`"
                                :value="`${index}-company`"
                            >
                                {{ value.association_code }} - {{ value.company_name }}
                            </a-select-option>
                        </template>
                    </a-select-opt-group>
                    <a-select-opt-group
                        v-if="
                            association_list.some((value) =>
                                value.name.toLowerCase().includes(filter_value.toLowerCase()),
                            )
                        "
                        label="Association"
                    >
                        <template v-for="(value, index) in association_list">
                            <a-select-option
                                v-if="value.name.toLowerCase().includes(filter_value.toLowerCase())"
                                :key="`${index}-association`"
                                :value="`${index}-association`"
                            >
                                {{ value.name }}
                            </a-select-option>
                        </template>
                    </a-select-opt-group>
                </a-select>
            </div>
            <div
                class="lg:w-1/3 w-full lg:mr-2 mb-2 lg:mb-0"
                v-if="showCompanyFilter"
                data-cy="upload-history-logs-filter"
            >
                <a-select
                    show-search
                    :placeholder="$t('globals.tables.filter')"
                    style="width: 100%"
                    option-filter-prop="children"
                    :filter-option="filterOption"
                    @change="handleCompanyFilterChange"
                    :allowClear="true"
                >
                    <a-select-option v-for="(value, index) in company_list" :key="index" :value="index">
                        {{ value.company_name }}
                    </a-select-option>
                </a-select>
            </div>
            <div class="w-auto" data-cy="created-date-field">
                <ui-date-range-picker-double v-model="created_date_range" @changeDates="handleDateFilterChange" />
            </div>
            <a-dropdown :trigger="['click']" v-if="recordsSelected">
                <a-button @click="(e) => e.preventDefault()" data-cy="btn-mass-selection">
                    {{ $t('company.upload_history.table.actions.actions') }}
                    <DownOutlined />
                </a-button>
                <template #overlay>
                    <a-menu @click="onActionButtons">
                        <a-menu-item key="resend_email" data-cy="actions-resend-email">
                            {{ $t('company.upload_history.table.actions.resend_email') }}
                        </a-menu-item>
                    </a-menu>
                </template>
            </a-dropdown>
        </div>
        <div class="list-table pt-2 md:pt-4 overflow-x-auto overflow-visible mt-1">
            <div class="w-full">
                <a-table
                    data-cy="upload-log-table"
                    :columns="getColumns"
                    :dataSource="data"
                    :rowKey="(record) => record.id"
                    :pagination="pagination"
                    :locale="locale"
                    :loading="loading"
                    :rowSelection="rowSelection"
                    @change="handleTableChange"
                    size="middle"
                    class="ant-table-striped"
                >
                    <template #bodyCell="{ column, record, text }">
                        <template v-if="column.key === 'actions'">
                            <a-button-group>
                                <a-tooltip placement="top" :title="$t('company.upload_history.open')">
                                    <a-button
                                        class="text-blue-400 hover:text-blue-400 focus:text-blue-400"
                                        @click="onTableAction(record.id, 'open')"
                                    >
                                        <FolderOpenOutlined />
                                    </a-button>
                                </a-tooltip>
                                <a-tooltip
                                    placement="top"
                                    :title="$t('company.upload_history.resend_email')"
                                    v-if="record.resend_confirmation"
                                >
                                    <a-button
                                        class="text-blue-400 hover:text-blue-400 focus:text-blue-400"
                                        @click="onTableAction(record.id, 'resend_email')"
                                    >
                                        <MailOutlined />
                                    </a-button>
                                </a-tooltip>
                            </a-button-group>
                        </template>
                        <template v-else-if="column.key === 'group_code'">
                            {{ record.association_code }} : {{ record.company_code }}
                        </template>
                        <template v-else-if="column.key === 'created_at'">
                            <span class="whitespace-nowrap">{{ dayjs(text).format('YYYY-MM-DD HH:mm:ss') }}</span>
                        </template>
                        <template v-else-if="column.key === 'mail_error'">
                            <span class="flex flex-row align-center justify-center">
                                <MinusCircleFilled v-if="text == '' || text == null" />
                                <ExclamationCircleFilled class="text-default-red" v-if="text" />
                            </span>
                        </template>
                        <template v-else>
                            {{ text }}
                        </template>
                    </template>
                </a-table>
                <span v-if="data.length > 0" data-cy="page-item-range" class="page-item-range flex">
                    {{
                        $t('globals.tables.page_range', {
                            pi_start: pageItemStart,
                            pi_end: pageItemEnd,
                            total: pagination.total,
                        })
                    }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import UiDateRangePickerDouble from '@/components/ui/DateRangePickerDouble.vue';
import {
    DownOutlined,
    ExclamationCircleFilled,
    FolderOpenOutlined,
    MailOutlined,
    MinusCircleFilled,
} from '@ant-design/icons-vue';

export default {
    components: {
        UiDateRangePickerDouble,
        DownOutlined,
        ExclamationCircleFilled,
        FolderOpenOutlined,
        MailOutlined,
        MinusCircleFilled,
    },
    data() {
        return {
            data: [],
            search_value: null,
            created_date_range: {
                start_date: null,
                end_date: null,
            },
            pagination: {
                pageSize: 10,
                showSizeChanger: false,
                total: 0,
            },
            locale: {
                emptyText: this.$t('globals.tables.no_data'),
            },
            loading: false,
            processing: false,
            current_filter: {},
            sorter: {},
            params: {},
            filter_value: '',
            company_list: [],
            association_list: [],
            selected_row_keys: [],
            dayjs: this.$dayjs,
        };
    },
    props: {
        company_id: String,
        association_id: String,
        is_admin_section: Boolean,
        is_association_section: Boolean,
    },
    mounted() {
        this.fetch({
            association_id: this.association_id,
            company_id: this.company_id,
            fetch_all: this.fetch_all,
        });
        if (this.fetch_all) {
            this.$http
                .get('/api/admin/list-companies-by-associations')
                .then((response) => {
                    this.company_list = response.data.data;
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVCAL02' }),
                    });
                });
            this.$http
                .get('/api/admin/list-associations')
                .then((response) => {
                    this.association_list = response.data.data;
                })
                .catch((error) => {
                    console.log(error);
                });
        }
        if (this.is_association_section) {
            this.$http
                .get('/api/admin/list-companies', {
                    params: {
                        association_id: this.$store.getters['user/currentAssociationId'],
                    },
                })
                .then((response) => {
                    this.company_list = response.data.data;
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVCL03' }),
                    });
                });
        }
    },
    computed: {
        getColumns() {
            return [
                {
                    key: 'actions',
                    width: 35,
                },
                {
                    key: 'group_code',
                    title: this.$t('globals.tables.upload_history.headers.group_code'),
                    sorter: false,
                },
                {
                    title: this.$t('globals.tables.upload_history.headers.created_date'),
                    key: 'created_at',
                    dataIndex: 'created_at',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.upload_history.headers.cover_count'),
                    key: 'cover_count',
                    dataIndex: 'cover_count',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.upload_history.headers.cover_merge_count'),
                    key: 'cover_merge_count',
                    dataIndex: 'cover_merge_count',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.upload_history.headers.invalid_cover_count'),
                    key: 'invalid_cover_count',
                    dataIndex: 'invalid_cover_count',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.upload_history.headers.mail_error'),
                    key: 'mail_error',
                    dataIndex: 'mail_error',
                },
                {
                    title: this.$t('globals.tables.upload_history.headers.file_source'),
                    key: 'file_source',
                    dataIndex: 'file_source',
                    sorter: true,
                },
            ];
        },
        pageItemStart() {
            let current_page = this.pagination.current;

            if (current_page === undefined) current_page = 1;

            return (current_page - 1) * this.pagination.pageSize + 1;
        },
        pageItemEnd() {
            let current_page = this.pagination.current,
                page_item_end = 0;

            if (current_page === undefined) current_page = 1;

            page_item_end = current_page * this.pagination.pageSize;

            if (this.pagination.total !== undefined && this.pagination.total < page_item_end) {
                return this.pagination.total;
            } else {
                return page_item_end;
            }
        },
        showFilter() {
            if (this.is_admin_section) {
                this.fetch_all = true;
                return true;
            } else {
                return false;
            }
        },
        showCompanyFilter() {
            if (this.is_association_section) {
                return true;
            } else {
                return false;
            }
        },
        rowSelection() {
            if (this.is_admin_section || this.is_association_section) {
                return {
                    selectedRowKeys: this.selected_row_keys,
                    onChange: this.onSelectChange,
                };
            } else {
                return null;
            }
        },
        recordsSelected() {
            return this.selected_row_keys.length > 0;
        },
        multipleRecordsSelected() {
            return this.selected_row_keys.length > 1;
        },
    },
    methods: {
        fetch(params = {}) {
            this.loading = true;
            this.params = { ...params };
            let start_date = null;
            let end_date = null;

            if (params.created_date_range !== undefined) {
                start_date = params.created_date_range.start_date
                    ? params.created_date_range.start_date.format('YYYY-MM-DD')
                    : null;
                end_date = params.created_date_range.end_date
                    ? params.created_date_range.end_date.format('YYYY-MM-DD')
                    : null;

                delete params.created_date_range;
            }

            this.$http
                .get('/api/cover-import-audit-logs', {
                    params: {
                        start_date: start_date,
                        end_date: end_date,
                        ...params,
                    },
                })
                .then((response) => {
                    const pagination = { ...this.pagination };

                    // Read total count from server
                    pagination.total = response.data.meta.total_count;

                    this.loading = false;
                    this.data = response.data.data;
                    this.pagination = pagination;
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVULF01' }),
                        });
                    }
                });
        },
        onTableAction(id, key) {
            this.$emit('onTableAction', {
                id: id,
                key: key,
            });
        },
        onActionButtons(event) {
            this.$emit('onActionButtons', event, this.selected_row_keys);
        },
        handleTableChange(pagination, filters, sorter) {
            const pager = { ...this.pagination };

            pager.current = pagination.current;

            this.pagination = pager;
            this.current_filter = filters;
            this.sorter = sorter;

            this.fetch({
                ...this.params,
                results: pagination.pageSize,
                page: pagination.current,
                sortField: sorter.field,
                sortOrder: sorter.order,
                ...filters,
            });
        },
        onSearch(input) {
            let regex = /^[a-zA-Z0-9\s\(\)_-]+$/;

            if (regex.test(input) || input === '') {
                if (input === '') {
                    this.search_value = null;
                } else {
                    this.search_value = input;
                }

                this.onSelectChange([]);

                this.fetch({
                    search_value: this.search_value,
                    company_id: this.params.company_id,
                    association_id: this.params.association_id,
                    created_date_range: this.params.created_date_range,
                });
            } else {
                this.$notification.error({
                    message: this.$t('globals.tables.invalid_characters'),
                });
            }

            this.pagination.current = 1;
        },
        handleFilterSearch(value) {
            this.filter_value = value;
        },
        handleDateFilterChange() {
            this.onSelectChange([]);

            this.fetch({
                search_value: this.params.search_value,
                company_id: this.params.company_id,
                association_id: this.params.association_id,
                created_date_range: this.created_date_range,
            });

            this.pagination.current = 1;
        },
        handleFilterChange(value) {
            let company_id = null;
            let association_id = null;
            let fetch_all = null;

            if (value == undefined) {
                fetch_all = true;
            } else {
                if (value.includes('company')) {
                    company_id = this.company_list[parseInt(value)].company_id;
                    association_id = this.company_list[parseInt(value)].association_id;
                } else {
                    association_id = this.association_list[parseInt(value)].id;
                }
            }

            this.onSelectChange([]);

            this.fetch({
                fetch_all: fetch_all,
                search_value: this.search_value,
                company_id: company_id,
                association_id: association_id,
                created_date_range: this.params.created_date_range,
            });

            this.filter_value = '';
            this.pagination.current = 1;
        },
        handleCompanyFilterChange(value) {
            let company_id = null;

            if (value != undefined) {
                company_id = this.company_list[value].company_id;
            }

            this.onSelectChange([]);

            this.fetch({
                company_id: company_id,
                search_value: this.search_value,
                association_id: this.association_id,
                created_date_range: this.params.created_date_range,
            });

            this.pagination.current = 1;
        },
        filterOption(input, option) {
            return option.children()[0].children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
        onSelectChange(selected_row_keys, selected_rows) {
            this.selected_row_keys = selected_row_keys;
        },
        reload() {
            const filters = this.current_filter;

            this.fetch({
                ...this.params,
                results: this.pagination.pageSize,
                page: this.pagination.current,
                sortField: this.sorter.field,
                sortOrder: this.sorter.order,
                ...filters,
            });
        },
    },
};
</script>
